import { ChangeDetectionStrategy, Component, HostListener, Inject, OnDestroy, PLATFORM_ID, signal } from '@angular/core';
import { CommonModule, isPlatformServer } from '@angular/common';

import { ConfigurationService, ISegmentAppConfig } from '@sondermind/configuration';

import { SegmentPreviewService } from '../../services/segment-preview.service';
import { DevToolsPanelComponent } from '../dev-tools-panel/dev-tools-panel.component';

const LOCAL_STORAGE_KEY = 'sondermind-dev-tools-fab-visible';

@Component({
  selector: 'dev-tools-fab',
  standalone: true,
  imports: [
    CommonModule,
    DevToolsPanelComponent
  ],
  templateUrl: './dev-tools-fab.component.html',
  styleUrl: './dev-tools-fab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevToolsFabComponent implements OnDestroy {
  readonly enabled: boolean;
  readonly fabVisible = signal(false);

  constructor(
    public previewService: SegmentPreviewService,
    private configurationService: ConfigurationService<ISegmentAppConfig>,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    if (isPlatformServer(platformId)) {
      this.enabled = false;
      return;
    }

    const { env } = this.configurationService;
    this.enabled = !!env.app?.devToolsEnabled;

    if (this.enabled) {
      this.loadFabVisibility();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }

    if (event.ctrlKey && event.shiftKey && event.key === 'S') {
      event.preventDefault();
      this.toggleFabVisibility();
    }
  }

  ngOnDestroy(): void {
    // Cleanup handled by HostListener decorator
  }

  private toggleFabVisibility(): void {
    const newValue = !this.fabVisible();
    this.fabVisible.set(newValue);
    this.saveFabVisibility(newValue);
  }

  private loadFabVisibility(): void {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored === 'true') {
        this.fabVisible.set(true);
      }
    } catch (err) {
      // localStorage might not be available
    }
  }

  private saveFabVisibility(visible: boolean): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, visible.toString());
    } catch (err) {
      // localStorage might not be available
    }
  }
}
